import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Youtube, Moon, Sun, Instagram, Facebook, Search, ShoppingCart, Menu, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { mediaUrl } from "@/lib/api";
import { TikTokIcon, XIcon } from "@/components/site/Icons";
import LanguageSelector from "@/components/site/GoogleTranslate";

export default function Header() {
  const { settings, t, cart, search, setSearch, theme, setTheme } = useSite();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const s = settings || {};

  const onSearch = (e) => {
    e.preventDefault();
    navigate("/excursiones");
    setOpen(false);
  };

  const linkCls = ({ isActive }) =>
    `text-[15px] font-medium transition-colors duration-200 hover:text-[var(--brand)] ${isActive ? "text-gray-900" : "text-gray-800"}`;

  return (
    <header className="sticky top-0 z-50 bg-white" data-testid="site-header">
      {/* Top bar */}
      <div className="border-b border-gray-100">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 h-[58px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="header-logo-link" className="flex items-center">
              {s.logo_url && <img src={mediaUrl(s.logo_url)} alt={s.site_name} className="h-11 w-auto object-contain" />}
            </Link>
            <LanguageSelector />
            <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle-btn"
              aria-label={theme === "dark" ? "Modo claro" : "Modo oscuro"} title={theme === "dark" ? "Modo claro" : "Modo oscuro"}
              className="w-8 h-8 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-colors">
              {theme === "dark" ? <Sun className="w-4 h-4 accent-text" /> : <Moon className="w-4 h-4 brand-text" />}
            </button>
          </div>
          <div className="hidden md:flex items-center gap-5 text-gray-600">
            <Link to="/#contacto" className="text-sm hover:text-[var(--brand)] transition-colors">{t("contact")}</Link>
            {s.social?.instagram && <a href={s.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-[var(--brand)] transition-colors"><Instagram className="w-4 h-4" /></a>}
            {s.social?.facebook && <a href={s.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-[var(--brand)] transition-colors"><Facebook className="w-4 h-4" /></a>}
            {s.social?.youtube && <a href={s.social.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="hover:text-[var(--brand)] transition-colors"><Youtube className="w-4 h-4" /></a>}
            {s.social?.x && <a href={s.social.x} target="_blank" rel="noreferrer" aria-label="X" className="hover:text-[var(--brand)] transition-colors"><XIcon className="w-3.5 h-3.5" /></a>}
            {s.social?.tiktok && <a href={s.social.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" className="hover:text-[var(--brand)] transition-colors"><TikTokIcon className="w-4 h-4" /></a>}
          </div>
          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menú">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {/* Main nav */}
      <div className="border-b border-gray-100 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 h-[62px] flex items-center justify-between gap-6">
          <form onSubmit={onSearch} className="relative w-full max-w-[470px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="header-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search")}
              className="w-full h-10 rounded-full border border-gray-200 pl-10 pr-4 text-sm outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition-[border-color,box-shadow]"
            />
          </form>
          <nav className="hidden md:flex items-center gap-6">
            <NavLink to="/" end className={linkCls} data-testid="nav-home">{t("home")}</NavLink>
            <NavLink to="/excursiones" className={linkCls} data-testid="nav-tours">{t("tours")}</NavLink>
            <Link to="/#contacto" className="text-[15px] font-medium text-gray-800 hover:text-[var(--brand)] transition-colors" data-testid="nav-contact">{t("contact")}</Link>
            <Link to="/admin" className="text-[15px] text-gray-500 hover:text-[var(--brand)] transition-colors" data-testid="nav-admin">{t("admin")}</Link>
          </nav>
          <Link to="/carrito" className="relative p-2 text-gray-800 hover:text-[var(--brand)] transition-colors" data-testid="header-cart-btn" aria-label={t("cart")}>
            <ShoppingCart className="w-6 h-6" />
            {cart.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full accent-bg text-[var(--accent-fg)] text-[11px] font-bold flex items-center justify-center" data-testid="cart-count">
                {cart.length}
              </span>
            )}
          </Link>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-white border-b border-gray-100 px-6 py-4 flex flex-col gap-4 fade-up" data-testid="mobile-menu">
          <Link to="/" onClick={() => setOpen(false)} className="font-medium">{t("home")}</Link>
          <Link to="/excursiones" onClick={() => setOpen(false)} className="font-medium">{t("tours")}</Link>
          <Link to="/#contacto" onClick={() => setOpen(false)} className="font-medium">{t("contact")}</Link>
          <Link to="/admin" onClick={() => setOpen(false)} className="text-gray-500">{t("admin")}</Link>
        </div>
      )}
    </header>
  );
}
